import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { LoadingPanel } from "../../components/layout/Loading";
import _debounce from "lodash/debounce";
import { getAllContact } from "./services/contact.services";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { setFilterContact } from "./contactSlice";
import { TextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import moment from "moment";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { checkAcessRights } from "../../_helper/helper";
import { EXCEL_EXPORT_ACCESS } from "../../_contstants/common";
import ExpandableText from "../../components/common/ExpandableText";

const Contact: React.FC = () => {
  const isExcelAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS
  );

  const handleExportExcel = () => {
    const datetime = moment().format("DD-MM-YYYY HH-mm");
    const table = document.querySelector(
      ".contact-excel-export"
    ) as HTMLTableElement;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(table);

    const remarksColIndex = Array.from(table.querySelectorAll("th")).findIndex(
      (th) => th.textContent?.trim().toLowerCase() === "remarks"
    );

    if (remarksColIndex !== -1) {
      const range = XLSX.utils.decode_range(ws["!ref"] || "");
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: row,
          c: remarksColIndex,
        });
        const cell = ws[cellAddress];
        if (
          cell &&
          typeof cell.v === "string" &&
          cell.v.includes("Show More")
        ) {
          cell.v = cell.v.replace(/Show More\s*$/i, "").trim();
        } else if (
          cell &&
          typeof cell.v === "string" &&
          cell.v.includes("Show Less")
        ) {
          cell.v = cell.v.replace(/Show Less\s*$/i, "").trim();
        }
      }
    }

    const colWidths: number[] = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const cells = row.querySelectorAll("th, td");
      cells.forEach((cell, colIndex) => {
        const cellContent = cell.textContent || "";
        const cellLength = cellContent.length;

        colWidths[colIndex] = Math.max(
          colWidths[colIndex] || 10,
          cellLength + 2
        );
      });
    });

    ws["!cols"] = colWidths.map((width) => ({ wch: width }));

    Object.keys(ws).forEach((key) => {
      if (!key.startsWith("!")) {
        const cell = ws[key];
        if (cell.z !== "m/d/yy") {
          if (typeof cell.v === "number") {
            const isDate = cell.t === "d";
            if (!isDate) {
              cell.t = "s";
              cell.v = `${cell.v}`;
            }
          }
        } else if (cell.z === "m/d/yy") {
          cell.z = "DD-MMM-YYYY";
        }
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, `Inquiries`);

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, `Amazon Uniform Inquiry List ${datetime}`);
  };

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Inquiry List
          </Typography.h4>
          {isExcelAccess && (
            <Button
              style={{ marginRight: 5 }}
              type="button"
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={handleExportExcel}
            >
              Excel Export
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <ContactGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const ContactGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const ContactList = useAppSelector((state) => state.contact.ContactList);
  const loading = useAppSelector((state) => state.contact.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterContact: any = useAppSelector(
    (state) => state.contact.FilterContact
  );
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  useEffect(() => {
    const payload = {
      ...FilterContact,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllContact(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 3 || props.index === 4 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        className="table-bordered responsive-table contact-excel-export"
        filter={filter}
        data={filter ? filterBy(ContactList, filter) : ContactList}
        onFilterChange={handleFilterChange}
        total={ContactList[0]?.itemcount || ContactList.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="Inquirydate"
          title="Date"
          cell={(props) => (
            <td>
              {props.dataItem.Inquirydate
                ? moment(props.dataItem.Inquirydate, "DD/MM/YYYY").format(
                    "DD-MMM-YYYY"
                  )
                : ""}
            </td>
          )}
          width={150}
        />
        <Column
          field="Name"
          title="Name"
          width={200}
          filterCell={CommonFilterCell}
        />
        <Column
          field="Email"
          title="Email ID"
          filterCell={CommonFilterCell}
          width={300}
        />
        <Column
          field="MobileNo"
          title="Mobile No."
          filterCell={CommonFilterCell}
          width={150}
        />
        <Column
          field="Remarks"
          title="Remarks"
          width={300}
          cells={{
            data: (props) => <ExpandableText text={props.dataItem.Remarks} />,
          }}
        />
      </KendoGrid>
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterContact: any = useAppSelector(
    (state) => state.contact.FilterContact
  );
  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        Limit: +pageLimit,
        Skip: 0 * +pageLimit,
      };

      dispatch(getAllContact(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterContact,
        [inputField]: inputValue,
      };
      dispatch(setFilterContact(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterContact, [props.field]: "" };
    dispatch(setFilterContact(updatedFilters));

    const payload = {
      ...updatedFilters,

      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };

    dispatch(getAllContact(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterContact[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterContact[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

export default Contact;
